import React from 'react';
import {useTranslation} from 'react-i18next';
import {MetricCombobox} from '../../dashboard/MetricCombobox';
import {Check, EnvelopeSimple, Lock, Plus} from '../../icons';
import {ChipMenu, ChipOptions} from './ChipMenu';
import {ScopePicker} from './ScopePicker';
import {RecipientsPicker} from './RecipientsPicker';
import {lineMetricLabel, periodLabel} from '../ruleText';
import {
    CHANNEL_CHOICES,
    CHANNEL_WEBHOOK,
    channelsKey,
    CONDITION_PERIOD_CHANGE,
    customMetricsForCombobox,
    isLineMetric,
    limitsOf,
    lineMetricsForCombobox,
    OP_GT,
    OPERATORS,
    operatorKey,
    PERIOD_CHANGE_OPERATORS,
    recipientKey,
    recipientTypesFor,
    scopeChannels,
    scopeKey,
    SCOPE_ANY,
    scopeTypesFor,
    TEAM_RECIPIENT_TYPES,
    unitBadge,
    vsPeriodsOf,
    withOperator,
} from '../rulesModel';

// Los períodos que se ofrecen si `options` no los trae (backend viejo): los del Tablero.
const FALLBACK_PERIODS = ['TODAY', 'YESTERDAY', 'LAST_7_DAYS', 'THIS_WEEK', 'THIS_MONTH', 'THIS_PERIOD', 'LAST_30_DAYS'];

const channelValue = (channels) => [...(channels || [])].sort().join(',');

/**
 * La frase de la regla con chips: «Avisar por [correo] [a mí] cuando [el cliente] [Go Fishing] tenga
 * [ROAS] [por debajo de] [2 #] en [este período]». Cada chip abre su menú (porteado); la métrica es
 * el `MetricCombobox` de siempre, filtrado por los canales de las líneas del alcance; el valor se
 * escribe en el chip, con la unidad que fija la métrica ($ / % / #).
 *
 * Los tokens salen de `sentenceTokens` (`rulesModel`): la misma frase que se lee en la lista.
 *
 * `scopeLocked`: el alcance tiene ids que quien mira no ve (`hidden_count`): se lee, no se edita.
 * `staleScopeIds`: los ids guardados que ya no están en el roster (los marca el selector).
 * `marks`: «En tus palabras» (F4), por campo del borrador: `{kind: 'ai'}` (lo armó la IA) o
 * `{kind: 'ask'}` (quedó por completar) → clase `-nl-ai` / `-nl-ask` del chip.
 */
export function SentenceChips({tokens, draft, options, unit, errors, limits = null, scopeLocked = false,
                                  staleScopeIds = [], marks = {}, allowNoChannels = false, onlyMe = false, onChange}) {
    const {t} = useTranslation();
    const {maxRecipients, maxScopeIds} = limitsOf(limits);
    const webhookInfo = (options && options.channels && options.channels.webhook) || null;
    const webhookAvailable = !webhookInfo || webhookInfo.available !== false;
    const webhookConfigured = !webhookInfo || webhookInfo.configured !== false;

    const set = (patch) => onChange({...draft, ...patch});

    // En una agencia convertida una regla puede quedar sin canales: «Apagada» (v2, review C-av2-fe-centro-09).
    const choices = allowNoChannels ? [...CHANNEL_CHOICES, []] : CHANNEL_CHOICES;
    const channelOptions = choices.map((choice) => {
        if (choice.length === 0) return {value: '', label: t('alerts_center.builder.channel_none'), hint: null, disabled: false};
        const usesWebhook = choice.includes(CHANNEL_WEBHOOK);
        let hint = null;
        if (usesWebhook && !webhookAvailable) hint = t('alerts_center.builder.webhook_unavailable');
        else if (usesWebhook && !webhookConfigured) hint = t('alerts_center.builder.webhook_not_configured');
        return {value: channelValue(choice), label: t(channelsKey(choice)), hint,
            disabled: usesWebhook && !webhookAvailable};
    });

    const periods = options && Array.isArray(options.periods) && options.periods.length
        ? options.periods.map((period) => period.id) : FALLBACK_PERIODS;
    const vsPeriods = vsPeriodsOf(options);
    const lineMetric = isLineMetric(draft.metric);
    // El operador elige también el tipo de condición: «por encima de» es un umbral, «sube más de» una
    // comparación contra otro período (v2). Una métrica de la línea no se compara: su período es fijo.
    const operatorOptions = [
        ...OPERATORS.map((operator) => ({value: operator, label: t(operatorKey(operator))})),
        ...PERIOD_CHANGE_OPERATORS.map((operator) => ({
            value: operator, label: t(operatorKey(operator)), disabled: lineMetric,
            hint: lineMetric ? t('alerts_center.builder.op_change_line_hint') : null,
        })),
    ];
    const chooseMetric = (metric) => {
        // Pasar a una de la línea con una comparación armada la vuelve umbral: la de la línea no se compara.
        if (isLineMetric(metric) && draft.conditionType === CONDITION_PERIOD_CHANGE) {
            onChange({...withOperator(draft, OP_GT), metric});
            return;
        }
        set({metric});
    };

    // Un chip que no se elige, y dice por qué: el período de una métrica de la línea (siempre el suyo), los de ROAS /
    // ACOS de Dashbo contra el mes pasado (la columna fija del Tablero) o lo fijo de una de Dashbo (ronda 5: la
    // métrica, su operador y sus períodos), con su motivo en `token.hintKey`.
    const renderFixed = (token, index) => {
        if (!token.hintKey && token.slot !== 'period' && token.slot !== 'vsPeriod') {
            return <span key={index} className="alc-chip -static -locked" data-slot={token.slot}>{token.text}</span>;
        }
        const hintId = `alc-${token.slot === 'vsPeriod' ? 'vs-period' : token.slot}-fixed-hint`;
        return (
            <span key={index} className="info-icon-container alc-chip -static -locked -fixed" data-slot={token.slot}
                  tabIndex={0} aria-describedby={hintId}>
                {token.text}
                <span className="warning-tooltip -below">
                    <span className="warning-tooltip-body">
                        <span id={hintId} className="warning-tooltip-text">
                            {t(token.hintKey || 'alerts_center.builder.period_fixed_hint')}
                        </span>
                    </span>
                </span>
            </span>
        );
    };

    const renderChip = (token, index) => {
        // El correo de una agencia convertida: va siempre (UD-18, con candado).
        if (token.slot === 'channelEmail') {
            return (
                <span key={index} className="alc-ch -on -locked" data-slot="channelEmail"
                      aria-label={t('alerts_center.builder.email_always_label')}>
                    <EnvelopeSimple size={15} aria-hidden="true"/>
                    {token.text}
                    <Lock size={13} className="alc-ch__lock" aria-hidden="true"/>
                </span>
            );
        }
        // El webhook: un chip que se prende y se apaga, al lado del correo (nunca otro switch).
        if (token.slot === 'channelWebhook') {
            const toggle = () => set({channels: token.on
                ? (draft.channels || []).filter((channel) => channel !== CHANNEL_WEBHOOK)
                : [...(draft.channels || []).filter((channel) => channel !== CHANNEL_WEBHOOK), CHANNEL_WEBHOOK]});
            return (
                <button key={index} type="button" className={`alc-ch ${token.on ? '-on' : '-off'}`} data-slot="channelWebhook"
                        aria-pressed={token.on} disabled={token.disabled} onClick={toggle}>
                    {token.on ? <Check size={14} weight="bold" aria-hidden="true"/> : <Plus size={14} weight="bold" aria-hidden="true"/>}
                    {token.text}
                </button>
            );
        }
        if (token.fixed) return renderFixed(token, index);
        const error = Boolean(token.slot && errors[slotField(token.slot)]);
        // «En tus palabras»: la marca es una clase del chip (no un envoltorio), así quitarla no lo remonta.
        const mark = token.slot && marks && marks[slotField(token.slot)] ? marks[slotField(token.slot)].kind : null;
        const markClass = mark ? ` -nl-${mark}` : '';
        switch (token.slot) {
            case 'channels':
                return (
                    <ChipMenu key={index} slot="channels" mark={mark} label={token.text} empty={token.empty} error={error}
                              menuLabel={t('alerts_center.builder.menu_channels')}>
                        {({close}) => (
                            <ChipOptions options={channelOptions} value={channelValue(draft.channels)} close={close}
                                         onChoose={(value) => set({channels: value ? value.split(',') : []})}/>
                        )}
                    </ChipMenu>
                );
            case 'recipients':
                // Un no admin crea sólo «a mí» (ronda 4, D13): el chip se lee y no se elige.
                if (onlyMe) return <span key={index} className="alc-chip -static -locked" data-slot="recipients">{token.text}</span>;
                return (
                    <ChipMenu key={index} slot="recipients" mark={mark} label={token.text} error={error && draft.recipients.type !== 'USERS'}
                              menuLabel={t('alerts_center.builder.menu_recipients')}>
                        {({close}) => (
                            <ChipOptions close={close} value={draft.recipients.type}
                                         options={recipientTypesFor(options).map((type) => ({value: type, label: t(recipientKey(type))}))}
                                         onChoose={(type) => set({recipients: {
                                             ...draft.recipients,
                                             type,
                                             userIds: type === 'USERS' ? draft.recipients.userIds : [],
                                             // Las bajas son de las reglas de equipo: al pasar a «a mí» o a personas, se van.
                                             excludedUserIds: TEAM_RECIPIENT_TYPES.includes(type) ? (draft.recipients.excludedUserIds || []) : [],
                                         }})}/>
                        )}
                    </ChipMenu>
                );
            case 'recipientExcept':
                // «salvo …» (ronda 5, D17): a quiénes del equipo no les llega; lo elige el admin.
                return (
                    <ChipMenu key={index} slot="recipientExcept" label={token.text} empty={token.empty}
                              menuLabel={t('alerts_center.builder.menu_except')}>
                        {() => (
                            <RecipientsPicker userIds={draft.recipients.excludedUserIds || []} options={options} max={maxRecipients}
                                              onChange={(excludedUserIds) => set({recipients: {...draft.recipients, excludedUserIds,
                                                  excludedKnown: true}})}/>
                        )}
                    </ChipMenu>
                );
            case 'recipientUsers':
                return (
                    <ChipMenu key={index} slot="recipientUsers" mark={mark} label={token.text} empty={token.empty} error={error}
                              menuLabel={t('alerts_center.builder.menu_people')}>
                        {() => (
                            <RecipientsPicker userIds={draft.recipients.userIds} options={options} max={maxRecipients}
                                              onChange={(userIds) => set({recipients: {...draft.recipients, userIds}})}/>
                        )}
                    </ChipMenu>
                );
            case 'scopeType':
                if (scopeLocked) return <span key={index} className="alc-chip -static -locked" data-slot="scopeType">{token.text}</span>;
                return (
                    <ChipMenu key={index} slot="scopeType" mark={mark} label={token.text}
                              menuLabel={t('alerts_center.builder.menu_scope')}>
                        {({close}) => (
                            <ChipOptions close={close} value={draft.scope.type}
                                         options={scopeTypesFor(options)
                                             // Una de Dashbo no tiene creador: «cualquier línea que yo vea» no aplica (R, 400).
                                             .filter((type) => !(draft.systemLocked && type === SCOPE_ANY))
                                             .map((type) => ({value: type, label: t(scopeKey(type))}))}
                                         onChoose={(type) => set({scope: {type, ids: type === draft.scope.type ? draft.scope.ids : []}})}/>
                        )}
                    </ChipMenu>
                );
            case 'scopeValue':
                if (scopeLocked) return <span key={index} className="alc-chip -static -locked" data-slot="scopeValue">{token.text}</span>;
                return (
                    <ChipMenu key={index} slot="scopeValue" mark={mark} label={token.text} empty={token.empty} error={error}
                              menuLabel={t('alerts_center.builder.menu_scope_value')}>
                        {() => (
                            <ScopePicker type={draft.scope.type} ids={draft.scope.ids} options={options}
                                         max={maxScopeIds} staleIds={staleScopeIds}
                                         onChange={(ids) => set({scope: {...draft.scope, ids}})}/>
                        )}
                    </ChipMenu>
                );
            case 'metric':
                return (
                    <span key={index} className={`alc-chip -metric${error ? ' -error' : ''}${markClass}`} data-slot="metric">
                        <MetricCombobox name="alert_rule_metric" value={draft.metric}
                                        customMetrics={customMetricsForCombobox(options)}
                                        lineMetrics={lineMetricsForCombobox(options).map((metric) => ({
                                            ...metric, display_name: lineMetricLabel(metric.metric_key, t) || metric.display_name,
                                        }))}
                                        channels={scopeChannels(draft.scope, options)}
                                        onChange={chooseMetric}/>
                    </span>
                );
            case 'operator':
                return (
                    <ChipMenu key={index} slot="operator" mark={mark} label={token.text} empty={token.empty} error={error}
                              menuLabel={t('alerts_center.builder.menu_operator')}>
                        {({close}) => (
                            <ChipOptions close={close} value={draft.operator} options={operatorOptions}
                                         onChoose={(operator) => onChange(withOperator(draft, operator, vsPeriods))}/>
                        )}
                    </ChipMenu>
                );
            case 'vsPeriod':
                if (token.fixed) return renderFixed(token, index);
                return (
                    <ChipMenu key={index} slot="vsPeriod" mark={mark} label={token.text} empty={token.empty} error={error}
                              menuLabel={t('alerts_center.builder.menu_vs_period')}>
                        {({close}) => (
                            <ChipOptions close={close} value={draft.vsPeriod}
                                         options={vsPeriods.map((period) => ({value: period, label: periodLabel(period, t)}))}
                                         onChoose={(vsPeriod) => set({vsPeriod})}/>
                        )}
                    </ChipMenu>
                );
            case 'value':
                return (
                    <span key={index} className={`alc-chip -value${error ? ' -error' : ''}${markClass}`} data-slot="value">
                        <input type="text" inputMode="decimal" autoComplete="off" value={draft.value}
                               placeholder={t('alerts_center.builder.value_placeholder')}
                               aria-label={t('alerts_center.builder.value_label')}
                               aria-invalid={error ? 'true' : 'false'}
                               onChange={(event) => set({value: event.target.value})}/>
                        <span className={`alc-chip__unit -${(unit || 'NUMBER').toLowerCase()}`}>{unitBadge(unit)}</span>
                    </span>
                );
            case 'period':
                if (token.fixed) return renderFixed(token, index);
                return (
                    <ChipMenu key={index} slot="period" mark={mark} label={token.text} empty={token.empty} error={error}
                              menuLabel={t('alerts_center.builder.menu_period')}>
                        {({close}) => (
                            <ChipOptions close={close} value={draft.period}
                                         options={periods.map((period) => ({value: period, label: periodLabel(period, t)}))}
                                         onChoose={(period) => set({period})}/>
                        )}
                    </ChipMenu>
                );
            default:
                return <span key={index} className="alc-chip -static">{token.text}</span>;
        }
    };

    return (
        <div className="alc-sentence" data-testid="alc-sentence">
            {tokens.map((token, index) => (token.type === 'chip'
                ? renderChip(token, index)
                : <span key={index} className="alc-sentence__word">{token.text}</span>))}
        </div>
    );
}

// El campo del borrador (y del error) al que corresponde cada chip.
export function slotField(slot) {
    if (slot === 'recipientUsers') return 'recipients';
    if (slot === 'scopeValue' || slot === 'scopeType') return 'scope';
    return slot;
}
